import { formatShortcut, isMac } from '../core/os.js';

let tooltipEl = null;
let activeTarget = null;
let delayTimer = null;

const HOVER_DELAY_MS = 120; // Fast 120ms show delay instead of native 1000ms

const ELEMENT_SHORTCUTS = {
  'run-btn': { mac: '⌘Enter', win: 'Ctrl+Enter' },
  'export-btn': { mac: '⌘E', win: 'Ctrl+E' },
  'inspect-btn': { mac: '⌘I', win: 'Ctrl+I' },
  'live-btn': { mac: '⌥L', win: 'Alt+L' }
};

export function getElementTooltipWithShortcut(el, baseTitle) {
  if (!el || !baseTitle) return baseTitle;
  const shortcutDef = ELEMENT_SHORTCUTS[el.id];
  if (shortcutDef) {
    const badge = formatShortcut(shortcutDef.mac, shortcutDef.win);
    if (!baseTitle.includes(badge)) {
      return `${baseTitle} (${badge})`;
    }
  }
  return baseTitle;
}

export function registerTooltipFeature() {
  try {
    // 1. Dynamic title-to-tooltip conversion
    const elementsWithTitle = document.querySelectorAll('[title]');
    elementsWithTitle.forEach((el) => {
      const title = el.getAttribute('title');
      if (title && !el.id.includes('preview-iframe')) {
        const enrichedTitle = getElementTooltipWithShortcut(el, title);
        el.setAttribute('data-tooltip', enrichedTitle);
        el.removeAttribute('title');
        if (ELEMENT_SHORTCUTS[el.id]) {
          const ariaKey = isMac() ? ELEMENT_SHORTCUTS[el.id].mac : ELEMENT_SHORTCUTS[el.id].win;
          el.setAttribute('aria-keyshortcuts', ariaKey);
        }
      }
    });

    // 2. Create the single shared tooltip element
    tooltipEl = document.createElement('div');
    tooltipEl.id = 'custom-tooltip';
    tooltipEl.className = 'custom-tooltip';
    tooltipEl.setAttribute('aria-hidden', 'true');
    document.body.appendChild(tooltipEl);

    // 3. Delegate event listeners on body
    document.body.addEventListener('pointerover', onPointerOver);
    document.body.addEventListener('pointerout', onPointerOut);
    document.body.addEventListener('pointermove', onPointerMove);
  } catch (e) {
    console.error('Failed to initialize custom tooltips:', e);
  }
}

function onPointerOver(event) {
  const target = event.target.closest('[title], [data-tooltip]');
  if (!target) return;

  // Convert/sync dynamic or lazy titles
  const title = target.getAttribute('title');
  if (title && !target.id.includes('preview-iframe')) {
    const enrichedTitle = getElementTooltipWithShortcut(target, title);
    target.setAttribute('data-tooltip', enrichedTitle);
    target.removeAttribute('title');
    if (ELEMENT_SHORTCUTS[target.id]) {
      const ariaKey = isMac() ? ELEMENT_SHORTCUTS[target.id].mac : ELEMENT_SHORTCUTS[target.id].win;
      target.setAttribute('aria-keyshortcuts', ariaKey);
    }
  }

  // Prevent flicker/re-triggering if we move within the same active target
  if (activeTarget === target) return;

  activeTarget = target;
  clearTimeout(delayTimer);

  delayTimer = setTimeout(() => {
    if (activeTarget !== target) return;
    const text = target.getAttribute('data-tooltip');
    if (!text || !tooltipEl) return;

    tooltipEl.textContent = text;
    tooltipEl.style.display = 'block';
    updatePosition(event.clientX, event.clientY);
  }, HOVER_DELAY_MS);
}

function onPointerMove(event) {
  if (!activeTarget || !tooltipEl || tooltipEl.style.display !== 'block') return;
  updatePosition(event.clientX, event.clientY);
}

function onPointerOut(event) {
  if (!activeTarget) return;

  // If the pointer is moving to a child element inside our active target, ignore.
  if (event.relatedTarget && activeTarget.contains(event.relatedTarget)) {
    return;
  }

  activeTarget = null;
  clearTimeout(delayTimer);
  if (tooltipEl) {
    tooltipEl.style.display = 'none';
  }
}

function updatePosition(x, y) {
  if (!tooltipEl) return;

  const offsetLeft = 12;
  const offsetTop = 15;
  let targetX = x + offsetLeft;
  let targetY = y + offsetTop;

  // Viewport bounds collision checking
  const rect = tooltipEl.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  if (targetX + rect.width > viewportWidth - 8) {
    targetX = x - rect.width - 8; // flip to left side of cursor
  }
  if (targetY + rect.height > viewportHeight - 8) {
    targetY = y - rect.height - 8; // flip above cursor
  }

  tooltipEl.style.left = `${Math.max(4, targetX)}px`;
  tooltipEl.style.top = `${Math.max(4, targetY)}px`;
}
